import {
  extractProductHardwareGuides,
  stripProductHardwareMetadata,
  type HardwareGuides,
} from '../assets/hardwareGuides';

export interface PreparedHardwareArtwork {
  viewBox: string;
  width: number;
  height: number;
  hardwareGuides: HardwareGuides;
  /** SVG document with placement metadata removed, for painting. */
  paintSvg: string;
  /** Object URL or public path used by `<image href>`. Set by the loader hook. */
  href?: string;
}

function parseViewBox(root: Element): string | null {
  const authored = root.getAttribute('viewBox')?.trim();
  if (authored) return authored;
  const width = Number.parseFloat(String(root.getAttribute('width') ?? ''));
  const height = Number.parseFloat(String(root.getAttribute('height') ?? ''));
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return `0 0 ${width} ${height}`;
  }
  return null;
}

function parseViewBoxSize(viewBox: string): { width: number; height: number } | null {
  const parts = viewBox.trim().split(/[\s,]+/).map(Number);
  if (
    parts.length === 4 &&
    parts.every((value) => Number.isFinite(value)) &&
    parts[2] > 0 &&
    parts[3] > 0
  ) {
    return { width: parts[2], height: parts[3] };
  }
  return null;
}

function serializeSvg(root: Element): string {
  const serialized = new XMLSerializer().serializeToString(root);
  return serialized.includes('xmlns=')
    ? serialized
    : serialized.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
}

/**
 * Read authored size and product placement metadata from a hardware SVG.
 * Markup used for painting has `#hardware-guides` and labelled anchors removed.
 */
export function prepareHardwareArtwork(svgText: string): PreparedHardwareArtwork | null {
  const document = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  if (document.querySelector('parsererror')) return null;
  const root = document.documentElement;
  if (root.tagName.toLowerCase() !== 'svg') return null;
  const viewBox = parseViewBox(root);
  if (!viewBox) return null;
  const size = parseViewBoxSize(viewBox);
  if (!size) return null;

  const hardwareGuides = extractProductHardwareGuides(root);
  root.querySelectorAll('namedview, sodipodi\\:namedview').forEach((node) => node.remove());
  stripProductHardwareMetadata(root);

  return {
    viewBox,
    width: size.width,
    height: size.height,
    hardwareGuides,
    paintSvg: serializeSvg(root),
  };
}
